import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { motion, useReducedMotion } from 'framer-motion';
import { FiArrowRight, FiChevronDown, FiExternalLink } from 'react-icons/fi';
import { images } from '../../constants';
import allProjectsData from '../Projects/ProjectsData';
import NavModal from './NavModal';
import ShareButton from './ShareButton';

const RESUME_URL = 'https://drive.google.com/file/d/1mHkAQJHt2OP0qD8d50Hdhox9Am6KTv3_/view?usp=sharing';
const SLIDE_MS = 4500;
const EASE_OUT = [0.23, 1, 0.32, 1];

const MenuIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const GitHubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.477 2 2 6.477 2 12c0 4.418 2.865 8.166 6.839 9.489.5.092.682-.217.682-.482 0-.237-.009-.868-.013-1.703-2.782.604-3.369-1.34-3.369-1.34-.454-1.156-1.11-1.463-1.11-1.463-.908-.62.069-.608.069-.608 1.003.07 1.531 1.03 1.531 1.03.892 1.529 2.341 1.087 2.91.831.092-.646.35-1.086.636-1.336-2.22-.253-4.555-1.11-4.555-4.943 0-1.091.39-1.984 1.029-2.683-.103-.253-.446-1.27.098-2.647 0 0 .84-.269 2.75 1.025A9.578 9.578 0 0 1 12 6.836a9.59 9.59 0 0 1 2.504.337c1.909-1.294 2.747-1.025 2.747-1.025.546 1.377.202 2.394.1 2.647.64.699 1.028 1.592 1.028 2.683 0 3.842-2.339 4.687-4.566 4.935.359.309.678.919.678 1.852 0 1.336-.012 2.415-.012 2.741 0 .267.18.579.688.481C19.138 20.163 22 16.418 22 12c0-5.523-4.477-10-10-10z" />
  </svg>
);

const LinkedInIcon = () => (
  <svg width="17" height="17" viewBox="0 0 24 24" fill="currentColor">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);

const socials = [
  { label: 'GitHub', href: 'https://github.com/Carshy', icon: <GitHubIcon /> },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/collins-musoko/', icon: <LinkedInIcon /> },
];

const KEYWORDS = ['React + TypeScript', 'Node.js & APIs', 'System Design', 'Production Delivery'];
const FOCUS_TAGS = ['Frontend', 'Backend & APIs', 'Full-stack delivery'];

// Featured slide is driven by the first project in ProjectsData,
// so updating the projects list keeps the hero in sync automatically.
const featured = allProjectsData[0];

const SLIDES = [
  {
    label: 'Current Focus',
    title: 'Enterprise HRMS & payroll',
    subtitle: 'React · Node.js · WebSockets',
    detail: 'Building secure, real-time HR and payroll workflows for 2,000+ employees at Nathan Digital.',
    accent: 'from-rose-500 to-orange-400',
  },
  {
    label: 'Status',
    title: 'Open to new opportunities',
    detail: 'Available for full-time engineering roles and freelance product work — remote or in Nairobi.',
    accent: 'from-emerald-500 to-teal-300',
  },
  {
    label: 'Featured Project',
    title: featured.title,
    subtitle: Object.values(featured.tags || {}).slice(0, 3).join(' · '),
    detail: featured.description,
    accent: 'from-rose-500 to-orange-400',
  },
];

const fadeUp = {
  hidden: { y: 24, opacity: 0 },
  visible: (i) => ({
    y: 0,
    opacity: 1,
    transition: { delay: 0.05 + i * 0.08, duration: 0.6, ease: EASE_OUT },
  }),
};

const iconBtn = 'glass-pill flex h-11 w-11 items-center justify-center rounded-full text-white/70 hover:border-white/25 hover:bg-white/10 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400';

// ── Circular portrait with outer ring + availability caption ───────────────
const HeroPortrait = () => (
  <motion.figure
    initial={{ scale: 0.96, opacity: 0 }}
    animate={{ scale: 1, opacity: 1 }}
    transition={{ duration: 0.6, ease: EASE_OUT }}
    className="relative mx-auto mb-10 h-[200px] w-[200px] sm:h-[230px] sm:w-[230px] xl:h-[260px] xl:w-[260px] [@media(min-width:1024px)_and_(max-height:820px)]:mb-7 [@media(min-width:1024px)_and_(max-height:820px)]:h-[170px] [@media(min-width:1024px)_and_(max-height:820px)]:w-[170px]"
  >
    <div className="absolute -inset-3 rounded-full border border-white/10" aria-hidden />
    <div
      className="absolute -inset-3 rounded-full bg-[conic-gradient(from_200deg,rgba(244,63,94,0.35),transparent_30%,transparent_70%,rgba(251,146,60,0.25))] opacity-60 [mask:radial-gradient(farthest-side,transparent_calc(100%-1px),#000_calc(100%-1px))]"
      aria-hidden
    />
    <div className="absolute inset-0 overflow-hidden rounded-full bg-white/5 shadow-[0_24px_80px_-12px_rgba(0,0,0,0.8)]">
      <picture>
        <source srcSet={images.collinsprofileWebp} type="image/webp" />
        <img
          src={images.collinsprofile}
          alt="Collins Musoko"
          width={260}
          height={260}
          loading="eager"
          decoding="async"
          className="h-full w-full object-cover object-[center_25%]"
        />
      </picture>
    </div>
    <figcaption className="glass glass-strong absolute -bottom-3 -left-6 rounded-xl px-4 py-2.5 sm:-left-10">
      <span className="flex items-center gap-2 text-[11px] font-medium tracking-wide text-white">
        <span className="relative flex h-1.5 w-1.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
          <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
        </span>
        Available for projects
      </span>
      <span className="mt-0.5 block text-[10px] text-white/50">Nairobi · GMT+3</span>
    </figcaption>
  </motion.figure>
);

// ── Auto-rotating focus card ───────────────────────────────────────────────
const FocusCard = ({ slides }) => {
  const [active, setActive] = useState(0);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const reduceMotion = useReducedMotion();
  const paused = hovered || focused || reduceMotion;

  useEffect(() => {
    if (paused) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % slides.length), SLIDE_MS);
    return () => clearInterval(id);
  }, [paused, slides.length, active]);

  return (
    <motion.div
      initial={{ y: 24, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.15, ease: EASE_OUT }}
      className="glass relative overflow-hidden rounded-2xl p-6 md:p-7 [@media(min-width:1024px)_and_(max-height:820px)]:p-5"
      onPointerEnter={(e) => { if (e.pointerType === 'mouse') setHovered(true); }}
      onPointerLeave={(e) => { if (e.pointerType === 'mouse') setHovered(false); }}
      onFocusCapture={(e) => { if (e.target.matches?.(':focus-visible')) setFocused(true); }}
      onBlurCapture={() => setFocused(false)}
    >
      <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-rose-500/15 blur-[60px]" aria-hidden />

      {/* Dots */}
      <div className="mb-5 flex items-center gap-1" role="tablist" aria-label="Highlights">
        {slides.map((s, i) => (
          <button
            type="button"
            key={s.label}
            role="tab"
            aria-selected={i === active}
            aria-label={`Show ${s.label}`}
            onClick={() => setActive(i)}
            className="group flex h-6 items-center justify-center px-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-400"
          >
            <span
              className={`h-1.5 rounded-full transition-all duration-300 ${i === active ? 'w-8 bg-rose-400' : 'w-1.5 bg-white/20 group-hover:bg-white/45'}`}
            />
          </button>
        ))}
      </div>

      {/* Slides — stacked in one grid cell so the card height never jumps */}
      <div className="grid" aria-live="polite">
        {slides.map((s, i) => {
          const isActive = i === active;
          return (
            <motion.div
              key={s.label}
              className="col-start-1 row-start-1"
              aria-hidden={!isActive}
              initial={false}
              animate={{ opacity: isActive ? 1 : 0, y: isActive ? 0 : 12 }}
              transition={reduceMotion ? { duration: 0 } : { duration: 0.3, ease: EASE_OUT }}
              style={{ pointerEvents: isActive ? 'auto' : 'none' }}
            >
              <span className="text-[11px] font-medium uppercase tracking-[0.18em] text-white/50">{s.label}</span>
              <h3 className="mb-1 mt-2 font-display text-2xl leading-tight text-white md:text-[1.75rem]">{s.title}</h3>
              {s.subtitle && <p className="mb-2 text-xs font-medium text-rose-300/80">{s.subtitle}</p>}
              <p className="line-clamp-3 text-sm leading-relaxed text-white/55">{s.detail}</p>
            </motion.div>
          );
        })}
      </div>

      {/* Progress */}
      <div className="mt-6 h-1 overflow-hidden rounded-full bg-white/[0.06]">
        {paused ? (
          <div className={`h-full w-full rounded-full bg-gradient-to-r ${slides[active].accent} opacity-40`} />
        ) : (
          <div
            key={active}
            className={`h-full rounded-full bg-gradient-to-r ${slides[active].accent}`}
            style={{ animation: `progressFill ${SLIDE_MS}ms linear forwards` }}
          />
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 border-t border-white/10 pt-5">
        <div className="mb-4 flex flex-wrap gap-2">
          {FOCUS_TAGS.map((t) => (
            <span key={t} className="glass-pill rounded-md px-2.5 py-1 text-[11px] font-medium text-white/60">{t}</span>
          ))}
        </div>
        <a
          href={RESUME_URL}
          target="_blank"
          rel="noreferrer"
          className="glass-pill inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm text-white hover:border-rose-400/40 hover:bg-rose-500/10"
        >
          View Resume
          <FiExternalLink className="h-3.5 w-3.5" />
        </a>
      </div>
    </motion.div>
  );
};

FocusCard.propTypes = {
  slides: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    detail: PropTypes.string.isRequired,
    accent: PropTypes.string.isRequired,
  })).isRequired,
};

// ── Home ───────────────────────────────────────────────────────────────────
const Home = () => {
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <>
      {/* Top bar */}
      <motion.div
        className="fixed left-0 right-0 top-0 z-50 flex items-center justify-between px-5 py-4 sm:px-8"
        initial={{ y: -16, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5, ease: 'easeOut' }}
      >
        <button type="button" onClick={() => setMenuOpen(true)} aria-label="Open menu" className={iconBtn}>
          <MenuIcon />
        </button>
        <div className="flex items-center gap-2">
          {socials.map((s) => (
            <a key={s.label} href={s.href} target="_blank" rel="noreferrer" aria-label={s.label} className={iconBtn}>
              {s.icon}
            </a>
          ))}
          <ShareButton className={iconBtn} />
        </div>
      </motion.div>

      <NavModal isOpen={menuOpen} onClose={() => setMenuOpen(false)} />

      {/* Hero */}
      <div className="relative h-full w-full overflow-y-auto overflow-x-hidden text-white">
        {/* Background: pure CSS, so nothing waits on a download */}
        <div
          className="pointer-events-none absolute inset-0"
          aria-hidden
          style={{
            background: 'radial-gradient(62% 70% at 72% 40%, rgba(244,63,94,0.10), transparent 72%), radial-gradient(48% 58% at 12% 14%, rgba(255,240,230,0.06), transparent 74%), #0a0907',
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-60 [mask-image:radial-gradient(70%_60%_at_50%_40%,#000,transparent)]"
          aria-hidden
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
            backgroundSize: '64px 64px',
          }}
        />

        <div className="relative z-10 mx-auto grid min-h-full w-full max-w-7xl items-center gap-14 px-6 pb-16 pt-24 sm:px-10 [@media(min-width:1024px)_and_(max-height:820px)]:pb-8 [@media(min-width:1024px)_and_(max-height:820px)]:pt-20 lg:grid-cols-[1.15fr_0.85fr] lg:gap-16 lg:pl-12 lg:pr-20">
          {/* Left — intro */}
          <motion.div initial="hidden" animate="visible">
            <motion.span
              custom={0}
              variants={fadeUp}
              className="glass-pill inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-xs font-medium tracking-wide text-white/75"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              Full-stack engineering &amp; product delivery
            </motion.span>

            <motion.h1
              custom={1}
              variants={fadeUp}
              className="mt-7 bg-gradient-to-b from-white via-white to-white/60 bg-clip-text pb-2 font-display text-[clamp(3.25rem,8vw,7rem)] font-normal leading-[0.98] tracking-tight text-transparent"
            >
              Collins
              <br />
              Musoko
            </motion.h1>

            <motion.p
              custom={2}
              variants={fadeUp}
              className="mt-6 max-w-xl text-lg leading-relaxed text-white/60 sm:text-xl"
            >
              Full-Stack Software Engineer building scalable, production-ready
              web platforms — from polished React interfaces to secure Node.js APIs.
            </motion.p>

            <motion.div custom={3} variants={fadeUp} className="mt-9 flex flex-wrap items-center gap-3 sm:gap-4">
              <a
                href="#projects"
                className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-[#0a0907] shadow-[0_10px_30px_-10px_rgba(255,255,255,0.4)] transition hover:bg-white/90 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/30"
              >
                View Projects
                <FiArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </a>
              <a
                href="#contact"
                className="glass-pill inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white hover:border-white/25 hover:bg-white/10 focus:outline-none focus-visible:ring-4 focus-visible:ring-white/20"
              >
                Get in Touch
                <FiChevronDown className="h-4 w-4" />
              </a>
            </motion.div>

            <motion.ul
              custom={4}
              variants={fadeUp}
              className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-white/45"
            >
              {KEYWORDS.map((k) => (
                <li key={k} className="flex items-center gap-2.5">
                  <span className="h-1 w-1 rounded-full bg-rose-400/60" aria-hidden />
                  {k}
                </li>
              ))}
            </motion.ul>
          </motion.div>

          {/* Right — portrait + focus card */}
          <div className="mx-auto w-full max-w-md lg:ml-auto lg:mr-0">
            <HeroPortrait />
            <FocusCard slides={SLIDES} />
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
